import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  FiPlus,
  FiSearch,
  FiEdit2,
  FiTrash2,
  FiPlay,
  FiEye,
  FiClipboard,
  FiFilter,
  FiPrinter,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import Api from '../../../Api';
import { usePermission } from '../../../hooks/usePermission';
import { generateServiceOrderDocPdf } from '../../../utils/serviceOrderDocPdf';
import {
  PageComponent,
  Card,
  Table,
  Button,
  Input,
  Select,
  Modal,
  Skeletons,
  TableActions,
  MultiSelect,
} from '../../../components';

import ServiceOrderModal from './ServiceOrderModal';
import NewExecutionModal from './NewExecutionModal';
import ViewExecutionModal from './ViewExecutionModal';
import '../../CompanyManager/style.css';
import './style.css';

const SERVICE_TYPE_LABELS = {
  cortesia: 'Cortesia',
  terceirizado: 'Terceirizado',
  padrao: 'Padrão',
};

const REQUEST_TYPE_LABELS = {
  corretiva: 'Corretiva',
  preventiva: 'Preventiva',
};

const STATUS_LABELS = {
  ativo: 'Ativo',
  inativo: 'Inativo',
};

const EXEC_STATUS_LABELS = {
  em_execucao: 'Em Execução',
  finalizado: 'Finalizado',
  cancelado: 'Cancelado',
};

export default function ServiceOrderRegistrations() {
  const permissions = usePermission('service_orders');
  const [selectedCompany] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('selectedCompany'));
    } catch (e) {
      return null;
    }
  });

  const [companies, setCompanies] = useState([]);
  const [companyFilterValues, setCompanyFilterValues] = useState(['all']);
  const [orders, setOrders] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  // Filters
  const [filterServiceType, setFilterServiceType] = useState('all');
  const [filterRequestType, setFilterRequestType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Order Modal
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // New Execution Modal
  const [isExecModalOpen, setIsExecModalOpen] = useState(false);
  const [orderForExec, setOrderForExec] = useState(null);

  // View Execution Modal
  const [isViewExecOpen, setIsViewExecOpen] = useState(false);
  const [viewExecId, setViewExecId] = useState(null);

  // Preloaded data
  const [preloadedData, setPreloadedData] = useState({
    employees: [],
    customers: [],
    suppliers: [],
    accountants: [],
    users: [],
    serviceStations: [],
    services: [],
  });

  // Delete Modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);



  const loadDependencies = useCallback(async (cId) => {
    if (!cId || cId === 'all') {
      setPreloadedData({
        employees: [], customers: [], suppliers: [], accountants: [], users: [], serviceStations: [], services: []
      });
      return;
    }
    try {
      const [empl, cust, supp, acc, serv, stations, users] = await Promise.all([
        Api.getEmployees(cId).catch(() => []),
        Api.getEntitiesByType(cId, 'customer').catch(() => []),
        Api.getSuppliers(cId).catch(() => []),
        Api.getAccountants(cId).catch(() => []),
        Api.getServices(cId).catch(() => []),
        Api.getServiceStations(cId).catch(() => []),
        Api.getEntitiesByType(cId, 'user').catch(() => [])
      ]);

      setPreloadedData({
        employees: Array.isArray(empl) ? empl : [],
        customers: Array.isArray(cust) ? cust : [],
        suppliers: Array.isArray(supp) ? supp : [],
        accountants: Array.isArray(acc) ? acc : [],
        users: Array.isArray(users) ? users : [],
        serviceStations: Array.isArray(stations) ? stations : [],
        services: Array.isArray(serv) ? serv : [],
      });
    } catch (e) {
      console.error("Error loading dependencies", e);
    }
  }, []);

  useEffect(() => {
    const allValue = 'all';
    const specificIds = companyFilterValues.filter(v => v !== allValue);

    if (specificIds.length === 1) {
      loadDependencies(specificIds[0]);
    } else {
      setPreloadedData({
        employees: [], customers: [], suppliers: [], accountants: [], users: [], serviceStations: [], services: [],
      });
    }
  }, [companyFilterValues, loadDependencies]);

  const loadData = useCallback(async () => {
    if (!selectedCompany?.id) return;

    setLoading(true);
    try {
      const allValue = 'all';
      const specificIds = companyFilterValues.filter(v => v !== allValue);
      const hasAllSelected = companyFilterValues.includes(allValue);

      let companiesToLoad;

      if (hasAllSelected || specificIds.length === 0) {
        companiesToLoad = 'all';
      } else if (specificIds.length === 1) {
        companiesToLoad = Number(specificIds[0]);
      } else {
        const ordersPerCompany = await Promise.all(
          specificIds.map(id =>
            Api.getServiceOrders(Number(id))
              .then(data => ({
                companyId: Number(id),
                orders: Array.isArray(data) ? data : [],
              }))
              .catch(() => ({ companyId: Number(id), orders: [] }))
          )
        );

        const allOrders = ordersPerCompany.flatMap(item =>
          item.orders.map(o => ({ ...o, _companyId: item.companyId }))
        );

        const execData = await Api.getServiceOrderExecutionsLight('all');
        const allExecs = Array.isArray(execData) ? execData : [];

        const execCounts = {};
        allExecs.forEach(ex => {
          execCounts[ex.service_order_id] = (execCounts[ex.service_order_id] || 0) + 1;
        });

        const ordersWithCounts = allOrders.map(o => ({
          ...o,
          executionCount: execCounts[o.id] || 0,
        }));

        setOrders(ordersWithCounts);
        setExecutions(allExecs);
        return;
      }

      let allOrders = [];
      let allExecs = [];

      if (companiesToLoad === 'all') {
        const [ordersData, execData] = await Promise.all([
          Api.getServiceOrders('all'),
          Api.getServiceOrderExecutionsLight('all'),
        ]);
        allOrders = Array.isArray(ordersData) ? ordersData : [];
        allExecs = Array.isArray(execData) ? execData : [];
      } else {
        const cId = companiesToLoad;
        const [ordersData, execData] = await Promise.all([
          Api.getServiceOrders(cId),
          Api.getServiceOrderExecutionsLight(cId),
        ]);
        allOrders = Array.isArray(ordersData) ? ordersData : [];
        allExecs = Array.isArray(execData) ? execData : [];
        allOrders.forEach(o => { o._companyId = cId; });
      }

      const execCounts = {};
      allExecs.forEach(ex => {
        execCounts[ex.service_order_id] = (execCounts[ex.service_order_id] || 0) + 1;
      });

      const ordersWithCounts = allOrders.map(o => ({
        ...o,
        executionCount: execCounts[o.id] || 0,
      }));

      setOrders(ordersWithCounts);
      setExecutions(allExecs);
      setHasLoadedOnce(true);
      setHasLoadedOnce(true);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar dados.');
    } finally {
      setLoading(false);
    }
  }, [selectedCompany, companyFilterValues]);

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterServiceType, filterRequestType, filterStatus, companyFilterValues]);

  async function loadCompanies() {
    try {
      const data = await Api.getCompanies();
      setCompanies(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    }
  }

  function handleCreate() {
    setSelectedOrder(null);
    setIsOrderModalOpen(true);
  }

  function handleEdit(order) {
    setSelectedOrder(order);
    setIsOrderModalOpen(true);
  }

  function handleNewExecution(order) {
    setOrderForExec(order);
    setIsExecModalOpen(true);
  }

  function handleDelete(order) {
    setOrderToDelete(order);
    setIsDeleteModalOpen(true);
  }

  async function handlePrintDoc(order) {
    const cId = order?._companyId || order?.company_id || selectedCompany?.id;
    if (!cId) {
      toast.error('Empresa não identificada para gerar o documento.');
      return;
    }

    try {
      const [company, target] = await Promise.all([
        Api.getCompany(cId).catch(() => null),
        Api.getEntityById(cId, order.target_type, order.target_id).catch(() => null),
      ]);

      await generateServiceOrderDocPdf({
        company,
        order,
        target,
      });
    } catch (error) {
      console.error(error);
      toast.error('Erro ao gerar PDF da OS.');
    }
  }

  async function confirmDelete() {
    if (!orderToDelete) return;
    const cId = orderToDelete._companyId || orderToDelete.company_id || selectedCompany?.id;
    if (!cId) return;
    setIsDeleting(true);
    try {
      await Api.deleteServiceOrder(cId, orderToDelete.id);
      setOrders(prev => prev.filter(o => o.id !== orderToDelete.id));
      toast.success('Ordem excluída!');
      setIsDeleteModalOpen(false);
      setOrderToDelete(null);
    } catch (error) {
      toast.error('Erro ao excluir ordem.');
    } finally {
      setIsDeleting(false);
    }
  }

  async function handleOrderFormSuccess(data) {
    const cId = data._companyId || selectedCompany?.id;
    if (!cId) return;
    try {
      const { _companyId, ...payload } = data;
      if (selectedOrder && selectedOrder.id) {
        const orderCId = selectedOrder._companyId || selectedOrder.company_id || cId;
        // Incluir company_id no payload para permitir mudança de empresa
        const updatePayload = { ...payload, company_id: cId };
        
        const updated = await Api.updateServiceOrder(orderCId, selectedOrder.id, updatePayload);
        if (updated && !updated.error) {
          setOrders(prev => prev.map(o => o.id === selectedOrder.id ? { ...updated, executionCount: o.executionCount } : o));
          toast.success('Ordem atualizada!');
        } else {
          toast.error(updated?.error || 'Erro ao atualizar.');
          return;
        }
      } else {
        const created = await Api.createServiceOrder(cId, payload);
        if (created && !created.error) {
          setOrders(prev => [...prev, { ...created, executionCount: 0 }]);
          toast.success('Ordem criada!');
        } else {
          toast.error(created?.error || 'Erro ao criar.');
          return;
        }
      }
      setIsOrderModalOpen(false);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao salvar ordem.');
    }
  }

  const filteredOrders = useMemo(() => {
    const allValue = 'all';
    const specificIds = companyFilterValues.filter(v => v !== allValue);
    const hasAllSelected = companyFilterValues.includes(allValue);

    return orders.filter(o => {
      const term = searchTerm.toLowerCase();
      const matchesSearch =
        (o.title || '').toLowerCase().includes(term) ||
        (o.target_name || '').toLowerCase().includes(term) ||
        (o.observation || '').toLowerCase().includes(term);
      const matchesServiceType = filterServiceType === 'all' || o.service_type === filterServiceType;
      const matchesRequestType = filterRequestType === 'all' || o.request_type === filterRequestType;
      const matchesStatus = filterStatus === 'all' || o.status === filterStatus;

      const orderCompanyId = o._companyId || o.company_id;
      const matchesCompany =
        hasAllSelected ||
        specificIds.length === 0 ||
        (orderCompanyId && specificIds.includes(String(orderCompanyId)));

      return matchesSearch && matchesServiceType && matchesRequestType && matchesStatus && matchesCompany;
    });
  }, [orders, searchTerm, filterServiceType, filterRequestType, filterStatus, companyFilterValues]);

  function handleCompanyFilterChange(newValues) {
    const allValue = 'all';
    const currentSelected = companyFilterValues;
    const isAllSelected = newValues.includes(allValue);
    const wasAllSelected = currentSelected.includes(allValue);

    let nextValues;

    if (isAllSelected && !wasAllSelected) {
      const allIds = companies.map(c => String(c.id));
      nextValues = [allValue, ...allIds];
    } else if (!isAllSelected && wasAllSelected) {
      nextValues = [];
    } else if (wasAllSelected && newValues.length < currentSelected.length) {
      const remaining = newValues.filter(v => v !== allValue);
      nextValues = remaining;
    } else {
      const specificIds = newValues.filter(v => v !== allValue);
      if (specificIds.length === companies.length && companies.length > 0) {
        nextValues = [allValue, ...specificIds];
      } else {
        nextValues = specificIds;
      }
    }

    setCompanyFilterValues(nextValues);
  }

  const totalItems = filteredOrders.length;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 1;
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const paginatedOrders = filteredOrders.slice(startIndex, startIndex + pageSize);

  const columns = [
    {
      id: 'target',
      header: 'Alvo',
      width: '2fr',
      cellClassName: 'manager-table-cell-primary',
      render: (item) => (
        <>
          <div className="manager-table-avatar">
            <FiClipboard size={18} />
          </div>
          <div className="manager-table-cell-text">
            <span className="manager-table-title">{item.target_name || `OS #${item.id}`}</span>
            <span className="manager-table-subtitle">
              {SERVICE_TYPE_LABELS[item.service_type] || item.service_type} | {REQUEST_TYPE_LABELS[item.request_type] || item.request_type}
            </span>
          </div>
        </>
      ),
    },
    {
      id: 'title',
      header: 'Título',
      width: '2fr',
      cellClassName: 'manager-table-cell',
      render: (item) => (
        <span className="manager-table-text">
          {item.title || '-'}
        </span>
      ),
    },
    {
      id: 'company',
      header: 'Empresa',
      width: '1.5fr',
      cellClassName: 'manager-table-cell',
      render: (item) => {
        // Se tiver companies (array do backend novo), usa ele
        if (item.companies && item.companies.length > 0) {
            return (
                <span className="manager-table-text">
                  {item.companies.map(c => c.trade_name || c.corporate_name || c.name).join(', ')}
                </span>
            );
        }

        // Fallback legado
        const company = companies.find(c => c.id === (item._companyId || item.company_id));
        return (
          <span className="manager-table-text">
            {company?.trade_name || company?.corporate_name || company?.name || 'N/A'}
          </span>
        );
      },
    },
    {
      id: 'services',
      header: 'Serviços',
      width: '1.5fr',
      cellClassName: 'manager-table-cell',
      render: (item) => (
        <span className="manager-table-subtitle">
          {item.services && item.services.length > 0 ? item.services.map(s => s.name).join(', ') : '-'}
        </span>
      ),
    },
    {
      id: 'next_exec',
      header: 'Próxima Execução',
      width: '1.2fr',
      cellClassName: 'manager-table-cell',
      render: (item) => (
        <span className="manager-table-subtitle">
          {item.next_execution_date
            ? new Date(item.next_execution_date + 'T00:00:00').toLocaleDateString('pt-BR')
            : '-'}
        </span>
      ),
    },
    {
      id: 'executions',
      header: 'Execuções',
      width: '0.8fr',
      cellClassName: 'manager-table-cell',
      render: (item) => (
        <span className="manager-table-subtitle">{item.executionCount || 0}</span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      width: '0.8fr',
      cellClassName: 'manager-table-cell',
      render: (item) => (
        <span className={`manager-tag ${item.status === 'ativo' ? 'manager-tag-success' : 'manager-tag-danger'}`}>
          {STATUS_LABELS[item.status] || item.status}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Ações',
      width: '0.6fr',
      cellClassName: 'manager-table-cell manager-table-col-actions',
      render: (item) => (
        <TableActions>
          <button type="button" onClick={(e) => { e.stopPropagation(); handlePrintDoc(item); }}>
            <FiPrinter size={16} /> Imprimir Doc. OS
          </button>
          {permissions.canEdit && (
            <button type="button" onClick={(e) => { e.stopPropagation(); handleEdit(item); }}>
              <FiEdit2 size={16} /> Editar
            </button>
          )}
          {permissions.canEdit && (
            <button type="button" onClick={(e) => { e.stopPropagation(); handleNewExecution(item); }}>
              <FiPlay size={16} /> Nova Execução
            </button>
          )}
          {permissions.canDelete && (
            <button type="button" className="action-danger" onClick={(e) => { e.stopPropagation(); handleDelete(item); }}>
              <FiTrash2 size={16} /> Excluir
            </button>
          )}
        </TableActions>
      ),
    },
  ];

  if (!permissions.canView) {
    return (
      <PageComponent authenticatedPage={true}>
        <div className="container">
          <div className="manager-header">
            <div className="manager-header-title">
              <div className="manager-header-icon">
                <FiClipboard size={22} />
              </div>
              <div>
                <h1>Acesso Negado</h1>
                <p>Você não tem permissão para visualizar este módulo.</p>
              </div>
            </div>
          </div>
        </div>
      </PageComponent>
    );
  }

  function SkeletonWrapper() {
    const { CompanyManagerSkeleton } = Skeletons;
    return <CompanyManagerSkeleton />;
  }

  return (
    <PageComponent authenticatedPage={true}>
      <div className="container">
        {loading && !hasLoadedOnce && <SkeletonWrapper />}

        {(!loading || hasLoadedOnce) && (
          <>
            <div className="manager-header">
              <div className="manager-header-title">
                <div className="manager-header-icon">
                  <FiClipboard size={22} />
                </div>
                <div>
                  <h1>Ordens de Serviço</h1>
                  <p>Cadastre e gerencie os modelos de ordem de serviço.</p>
                </div>
              </div>
              {permissions.canCreate && (
                <Button onClick={handleCreate} className="manager-header-button">
                  <FiPlus size={18} />
                  Nova Ordem
                </Button>
              )}
            </div>

            <Card className="manager-filters-card">
              <div className="manager-filters-header">
                <div className="manager-filters-title">
                  <FiFilter size={16} />
                  <span>Filtros</span>
                </div>
                {orders.length > 0 && (
                  <span className="manager-filters-count">
                    {filteredOrders.length} de {orders.length} ordens
                  </span>
                )}
              </div>
              <div className="manager-filters-grid-custom">
                <div className="manager-filter-group">
                  <label className="manager-filter-label">Empresa</label>
                  <MultiSelect
                    id="filter-company"
                    options={[
                      { value: 'all', label: 'TODAS' },
                      ...companies.map(c => ({
                        value: String(c.id),
                        label: c.corporate_name || c.trade_name || c.name || c.razao_social || c.nome_fantasia || `Empresa ${c.id}`,
                      })),
                    ]}
                    value={companyFilterValues}
                    onChange={handleCompanyFilterChange}
                    searchable
                  />
                </div>
                <div className="manager-filter-group">
                  <label className="manager-filter-label">Buscar</label>
                  <div className="manager-filter-search">
                    <Input
                      id="order-search"
                      placeholder="Buscar por título ou alvo..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      icon={FiSearch}
                    />
                  </div>
                </div>
                <div className="manager-filter-group">
                  <label className="manager-filter-label">Tipo de Serviço</label>
                  <Select
                    id="filter-service-type"
                    options={[
                      { value: 'all', label: 'Todos' },
                      { value: 'cortesia', label: 'Cortesia' },
                      { value: 'terceirizado', label: 'Terceirizado' },
                      { value: 'padrao', label: 'Padrão' },
                    ]}
                    value={filterServiceType}
                    onChange={e => setFilterServiceType(e.target.value)}
                  />
                </div>
                <div className="manager-filter-group">
                  <label className="manager-filter-label">Solicitação</label>
                  <Select
                    id="filter-request-type"
                    options={[
                      { value: 'all', label: 'Todas' },
                      { value: 'corretiva', label: 'Corretiva' },
                      { value: 'preventiva', label: 'Preventiva' },
                    ]}
                    value={filterRequestType}
                    onChange={e => setFilterRequestType(e.target.value)}
                  />
                </div>
                <div className="manager-filter-group">
                  <label className="manager-filter-label">Status</label>
                  <Select
                    id="filter-status"
                    options={[
                      { value: 'all', label: 'Todos' },
                      { value: 'ativo', label: 'Ativo' },
                      { value: 'inativo', label: 'Inativo' },
                    ]}
                    value={filterStatus}
                    onChange={e => setFilterStatus(e.target.value)}
                  />
                </div>
              </div>
            </Card>

            {orders.length === 0 ? (
              <div className="empty-state">
                <p>Nenhuma ordem de serviço cadastrada.</p>
              </div>
            ) : (
              <Table
                items={paginatedOrders}
                columns={columns}
                totalItems={totalItems}
                page={safePage}
                totalPages={totalPages}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={value => { setPageSize(value); setCurrentPage(1); }}
                getRowKey={item => item.id}
                emptyMessage="Nenhuma ordem encontrada com os filtros atuais."
              />
            )}
          </>
        )}
      </div>

      <ServiceOrderModal
        isOpen={isOrderModalOpen}
        onClose={() => setIsOrderModalOpen(false)}
        initialData={selectedOrder}
        onSuccess={handleOrderFormSuccess}
        companyId={selectedCompany?.id}
        companies={companies}
        preloadedEmployees={preloadedData.employees}
        preloadedCustomers={preloadedData.customers}
        preloadedSuppliers={preloadedData.suppliers}
        preloadedAccountants={preloadedData.accountants}
        preloadedUsers={preloadedData.users}
        preloadedServiceStations={preloadedData.serviceStations}
        preloadedServices={preloadedData.services}
      />

      <NewExecutionModal
        isOpen={isExecModalOpen}
        onClose={() => setIsExecModalOpen(false)}
        serviceOrder={orderForExec}
        companyId={orderForExec?._companyId || orderForExec?.company_id || selectedCompany?.id}
        onSuccess={() => { setIsExecModalOpen(false); loadData(); }}
      />

      <ViewExecutionModal
        isOpen={isViewExecOpen}
        onClose={() => setIsViewExecOpen(false)}
        executionId={viewExecId}
        companyId={selectedCompany?.id}
        onUpdate={loadData}
      />

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirmar exclusão"
        closeOnOverlayClick={!isDeleting}
      >
        <div className="delete-modal-content">
          <p>
            Tem certeza que deseja excluir a ordem{' '}
            {orderToDelete && (
              <span className="delete-modal-company-name">{orderToDelete.target_name || `OS #${orderToDelete.id}`}</span>
            )}?
          </p>
          <p>Essa ação não poderá ser desfeita.</p>
        </div>
        <div className="delete-modal-actions">
          <Button type="button" className="cancel-button" onClick={() => setIsDeleteModalOpen(false)} disabled={isDeleting}>
            Cancelar
          </Button>
          <Button type="button" onClick={confirmDelete} disabled={isDeleting}>
            {isDeleting ? 'Excluindo...' : 'Excluir'}
          </Button>
        </div>
      </Modal>
    </PageComponent>
  );
}
